// frontend/src/pages/auth/ResetPassword.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, Lock, CheckCircle, X } from 'react-feather';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Extract token from query parameters
    const params = new URLSearchParams(location.search);
    const resetToken = params.get('token');
    
    if (!resetToken) {
      setVerifying(false);
      setError('Reset token is missing');
      return;
    }
    
    // Verify the token is valid
    const verifyToken = async () => {
      try {
        await api.get(`/auth/verify-reset-token?token=${resetToken}`);
        setToken(resetToken);
        setTokenValid(true);
      } catch (err: any) {
        console.error('Invalid reset token:', err);
        setError('Password reset link is invalid or has expired');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [location]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await api.post('/auth/reset-password', { token, password });
      
      setSuccess(true);
      toast.success('Your password has been reset successfully');
    } catch (err: any) {
      console.error('Password reset error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to reset password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50">
        <Loader size="lg" />
        <p className="mt-4 text-gray-600">Verifying reset link...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Heart className="h-12 w-12 text-pink-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {success ? 'Password Reset Complete' : tokenValid ? 'Reset Your Password' : 'Invalid Reset Link'}
        </h2>
        {tokenValid && !success && (
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          {success ? (
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900">Password reset successful</h3>
                <div className="mt-2 px-2">
                  <p className="text-sm text-gray-500">
                    Your password has been reset successfully. You can now log in with your new password.
                  </p>
                </div>
                <div className="mt-5">
                  <Button
                    type="button"
                    fullWidth
                    onClick={() => navigate('/login')}
                  >
                    Go to login
                  </Button>
                </div>
              </div>
            </div>
          ) : !tokenValid ? (
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <X className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900">Invalid reset link</h3>
                <div className="mt-2 px-2">
                  <p className="text-sm text-red-500">
                    {error || 'Your password reset link is invalid or has expired.'}
                  </p>
                </div>
                <div className="mt-5">
                  <Link to="/forgot-password">
                    <Button
                      type="button"
                      fullWidth
                      variant="outline"
                    >
                      Request new reset link
                    </Button>
                  </Link>
                  <div className="mt-3">
                    <Link to="/login">
                      <Button
                        type="button"
                        fullWidth
                      >
                        Back to login
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-5 sm:p-6">
              {error && (
                <div className="mb-4">
                  <Alert
                    type="error"
                    message={error}
                    dismissible
                    onDismiss={() => setError(null)}
                  />
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  label="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                  helperText="Password must be at least 8 characters"
                />
                
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                  error={password !== confirmPassword && confirmPassword !== '' ? 'Passwords do not match' : undefined}
                />
                
                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  fullWidth
                >
                  Reset password
                </Button>
                
                <div className="flex items-center justify-center">
                  <div className="text-sm">
                    <Link to="/login" className="font-medium text-pink-600 hover:text-pink-500">
                      Back to login
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;